import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Percent } from "lucide-react";
import { formatBRL } from "./LoanDetailsDialog";
import { unpaidInstallments, interestShare } from "./LoansSummary";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export function computeMonthlyInterest(loans, year) {
  const rows = MONTHS.map((label, i) => ({ label, key: `${year}-${String(i + 1).padStart(2, "0")}`, interest: 0, received: 0, count: 0, scheduled: 0, scheduledCount: 0, revenue: 0 }));
  for (const l of loans) {
    const share = interestShare(l);
    if (l.status !== "paid") {
      for (const inst of unpaidInstallments(l)) {
        if (!inst.due || !inst.due.startsWith(String(year))) continue;
        const m = parseInt(inst.due.slice(5, 7), 10) - 1;
        if (m < 0 || m > 11) continue;
        rows[m].scheduled += inst.interest;
        rows[m].scheduledCount += 1;
        rows[m].revenue += inst.amount;
      }
    }
    for (const p of l.payments || []) {
      if (!p.date || !p.date.startsWith(String(year))) continue;
      const m = parseInt(p.date.slice(5, 7), 10) - 1;
      if (m < 0 || m > 11) continue;
      rows[m].received += p.amount || 0;
      rows[m].interest += p.interest_only ? p.amount || 0 : (p.amount || 0) * share;
      rows[m].count += 1;
    }
  }
  return rows;
}

export default function LoansMonthly({ loans, today, onMonthClick }) {
  const curYear = parseInt(today.slice(0, 4), 10);
  const curMonthKey = today.slice(0, 7);
  const [year, setYear] = useState(curYear);
  const rows = useMemo(() => computeMonthlyInterest(loans, year), [loans, year]);
  const yearInterest = rows.reduce((s, r) => s + r.interest, 0);
  const yearScheduled = rows.reduce((s, r) => s + r.scheduled, 0);
  const max = Math.max(0, ...rows.map((r) => Math.max(r.interest, r.scheduled)));

  return (
    <div data-testid="loans-monthly" className="rounded-xl border border-white/10 bg-[#121212] p-4 md:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-white">
            <Percent className="h-4 w-4 text-[#A78BFA]" />
            Juros recebidos por mês
          </h3>
          <p className="text-xs text-zinc-500">
            Clique em um mês para ver o extrato · {year}: recebido{" "}
            <span data-testid="loans-monthly-year-total" className="font-mono-num font-semibold text-[#A78BFA]">{formatBRL(yearInterest)}</span>
            {" · "}a receber{" "}
            <span data-testid="loans-monthly-year-scheduled" className="font-mono-num font-semibold text-[#D4FF00]">{formatBRL(yearScheduled)}</span>
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-white/10 bg-black/40 p-0.5">
          <button
            data-testid="loans-monthly-prev"
            onClick={() => setYear((y) => y - 1)}
            className="rounded-full p-1.5 text-zinc-400 hover:bg-white/5 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span data-testid="loans-monthly-year" className="min-w-[3.5rem] text-center font-mono-num text-sm font-bold text-white">{year}</span>
          <button
            data-testid="loans-monthly-next"
            onClick={() => setYear((y) => y + 1)}
            className="rounded-full p-1.5 text-zinc-400 hover:bg-white/5 hover:text-white"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {rows.map((r) => {
          const isCur = r.key === curMonthKey;
          const has = r.interest > 0;
          const hasSched = r.scheduled > 0;
          const intensity = max > 0 && has ? 0.15 + 0.55 * (r.interest / max) : 0;
          return (
            <button
              type="button"
              key={r.key}
              data-testid={`loans-month-${r.key}`}
              onClick={() => onMonthClick && onMonthClick(r.key)}
              className="flex flex-col justify-between rounded-lg border p-2.5 text-left hover:brightness-125 focus:outline-none focus:ring-1 focus:ring-[#A78BFA]"
              style={{
                borderColor: isCur ? "#A78BFA" : has ? "rgba(167,139,250,0.35)" : hasSched ? "rgba(212,255,0,0.35)" : "rgba(255,255,255,0.08)",
                borderStyle: !has && hasSched ? "dashed" : "solid",
                backgroundColor: has ? `rgba(167,139,250,${intensity.toFixed(2)})` : hasSched ? "rgba(212,255,0,0.06)" : "rgba(0,0,0,0.3)",
                boxShadow: isCur ? "0 0 0 1px #A78BFA inset" : "none",
              }}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[0.7rem] font-bold uppercase tracking-widest ${isCur ? "text-white" : "text-zinc-400"}`}>{r.label}</span>
                {r.count > 0 && <span className="text-[0.62rem] text-zinc-400">{r.count}×</span>}
              </div>
              <div className="mt-2">
                <div className="text-[0.55rem] uppercase tracking-widest text-zinc-500">Recebido</div>
                <div className="font-mono-num text-sm font-bold tabular-nums" style={{ color: has ? "#FFFFFF" : "#52525B" }}>
                  {has ? formatBRL(r.interest) : "—"}
                </div>
              </div>
              <div className="mt-1.5">
                <div className="text-[0.55rem] uppercase tracking-widest text-zinc-500">A receber</div>
                <div
                  data-testid={`loans-month-scheduled-${r.key}`}
                  className="font-mono-num text-xs font-bold tabular-nums"
                  style={{ color: hasSched ? "#D4FF00" : "#52525B" }}
                >
                  {hasSched ? formatBRL(r.scheduled) : "—"}
                  {hasSched && <span className="ml-1 text-[0.55rem] font-normal text-zinc-500">{r.scheduledCount}×</span>}
                </div>
              </div>
              <div className="mt-1.5 border-t border-white/5 pt-1">
                <div className="text-[0.55rem] uppercase tracking-widest text-zinc-500">Receita prev.</div>
                <div data-testid={`loans-month-revenue-${r.key}`} className="font-mono-num text-xs font-bold tabular-nums" style={{ color: r.revenue > 0 ? "#FFFFFF" : "#52525B" }}>
                  {r.revenue > 0 ? formatBRL(r.revenue) : "—"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
